import { Component, Input, OnInit } from "@angular/core";
import { VisualizationOptionsType } from "@arch/_shared/types/options-visualization";
import {
    CompanyEntity,
    PointEntity,
    SerieEntity,
    SerieType,
    VisualizationEntity
} from "@arch/domain/entity";
import { HighchartsRenderComponent } from "../../highcharts-render/highcharts-render.component";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import { SharedDsnModule } from "@app/shared/modules/shared-dsn.module";
import { FormatNumbers } from "@arch/_shared/utils/format-number.utils";
import { ColorsService } from "@arch/domain/application/services/colors.service";
import { GetSeriesByIdsController } from "@arch/presentation/controllers";

@Component({
    selector: "app-chart-custom-categories",
    imports: [HighchartsRenderComponent, SharedMaterialModule, SharedDsnModule],
    templateUrl: "./chart-custom-categories.component.html",
    styleUrl: "./chart-custom-categories.component.scss"
})
export class ChartCustomCategoriesComponent implements OnInit {
    @Input({ required: true }) visualization!: VisualizationEntity;
    @Input({ required: true }) options!: VisualizationOptionsType;
    @Input({ required: true }) series!: SerieEntity[];
    @Input({ required: true }) company!: CompanyEntity;
    @Input({ required: true }) points!: PointEntity[];

    public chartStructure: any = {};
    public isCustomStructure: boolean = true;

    constructor(readonly getSeriesByIdsController: GetSeriesByIdsController) {}

    ngOnInit(): void {
        this.setVisualization();
    }

    private setVisualization() {
        if (
            this.options.comparation.active ||
            this.options.budget ||
            this.options.projection ||
            this.options.goals
        ) {
            this.isCustomStructure = false;
            this.setChartStructureComparation();
        } else {
            this.setChartStructure();
        }
    }

    private setChartStructureComparation(): void {
        const structure: any = {
            title: {
                text: ""
            },
            credits: {
                enabled: false
            },
            exporting: {
                enabled: false
            },
            plotOptions: {
                series: {
                    borderWidth: 0
                },
                column: {
                    stacking: "normal",
                    cursor: "pointer"
                }
            },
            chart: {
                type: "column"
            },
            yAxis: { title: { text: "" } },
            legend: {
                enabled: true
            },
            series: this.visualization.props.component.configs.series.map(
                (s: any, i: number) => {
                    return {
                        _id: s._id,
                        type: s.type,
                        color: s.color
                    };
                }
            )
        };
        if (this.options.goals)
            structure.goals = this.visualization.props.component.configs.goals;

        this.visualization.setChartConfiguration(structure);
    }

    private setChartStructure(): void {
        const _formatValue = FormatNumbers.byUnit;
        const stacked = this.visualization.props?.component?.configs?.stacked;
        const seriesCategories = stacked
            ? this.gethighlightsSeries()
            : this.series;

        const structure = {
            title: {
                text: ""
            },
            credits: {
                enabled: false
            },
            exporting: {
                enabled: false
            },
            chart: {
                type: "column"
            },
            xAxis: {
                categories: this.getCategories(seriesCategories)
            },
            yAxis: { title: { text: "" } },
            legend: {
                enabled: false
            },
            series: this.getSeriesStructure(),
            plotOptions: {
                series: {
                    borderWidth: 0
                },
                column: {
                    stacking: "normal",
                    cursor: "pointer"
                }
            },
            tooltip: {
                backgroundColor: "#f4f4f4",
                style: {
                    color: "black",
                    font: "9pt Open Sans"
                },
                formatter: function (
                    this: Highcharts.TooltipFormatterContextObject
                ) {
                    const point = this.point as Highcharts.Point & {
                        unit?: string;
                    };
                    return `${stacked ? this.series.name : this.key}
                    ${_formatValue(point.unit, this.y, 2)}`;
                }
            }
        };

        structure.yAxis = this.formatYAxis({
            series: this.series,
            structure
        });

        this.chartStructure = structure;
    }

    private gethighlightsSeries(): SerieEntity[] {
        return this.getSeriesByIdsController.execute([
            this.visualization.props.highlightsSeriesIds
        ]);
    }

    private getCategories(series: SerieEntity[] = this.series) {
        if (!series) return [];
        return series.map((s) => s.props.name);
    }

    private getSeriesStructure(series: SerieEntity[] = this.series) {
        if (this.visualization.props?.component?.configs?.stacked)
            return this.modelerSeriesStacked(series);
        else return this.modelerSeriesNormal(series);
    }

    private modelerSeriesNormal(series = this.series) {
        if (!series) return [];

        return [
            {
                data: series.map((s, i) => {
                    return {
                        y: this._getPoint(s),
                        color: ColorsService.getColor(
                            this.visualization.props?.component?.configs
                                ?.series[i]?.color
                        ),
                        unit: s.props.unit
                    };
                })
            }
        ];
    }

    private modelerSeriesStacked(series: SerieEntity[] = this.series) {
        if (!series) return [];

        return series.map((s: SerieEntity, i: number) => {
            return {
                name: s.props.name,
                data: [
                    {
                        y: this._getPoint(s),
                        unit: s.props.unit,
                        color: ColorsService.getColor(
                            this.visualization.props?.component?.configs
                                ?.series[i].color
                        )
                    }
                ]
            };
        });
    }

    private _getPoint(
        serie: SerieEntity,
        points: PointEntity[] = this.points
    ): number {
        const point = points.find(
            (p: PointEntity) =>
                p.props.s === serie.props._id && +p.t === +this.options.endDate
        );

        return point?.props.m?.v || 0;
    }

    private formatYAxis({
        series = [],
        structure = {}
    }: { series?: any[]; structure?: any } = {}): any {
        if (!structure.yAxis || !series.length) return {};

        const _currencyLabelFormatter = () => {
            return function (this: { value: number }): string {
                const kFormatter = (num: number): string => {
                    if (Math.abs(num) < 1e6) {
                        return `${Math.sign(num) * +(Math.abs(num) / 1e3).toFixed(1)}k`;
                    } else {
                        return `${Math.sign(num) * +(Math.abs(num) / 1e6).toFixed(1)}M`;
                    }
                };

                if (Math.abs(this.value) > 999) {
                    return "R$ " + kFormatter(this.value);
                }

                return this.value.toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL"
                });
            };
        };

        const deepCopy = (obj: any): any => JSON.parse(JSON.stringify(obj));

        const units: Record<string, (axis: any) => any> = {
            currency: (serieYAxis) => {
                const axis = deepCopy(serieYAxis);
                axis.labels = axis.labels || {};
                axis.labels.formatter = _currencyLabelFormatter();
                return axis;
            },
            percentage: (serieYAxis) => {
                const axis = deepCopy(serieYAxis);
                axis.labels = axis.labels || {};
                delete axis.labels.formatter;
                axis.labels.format = "{value:.2f}%";
                return axis;
            },
            number: (serieYAxis) => {
                const axis = deepCopy(serieYAxis);
                axis.labels = axis.labels || {};
                delete axis.labels.formatter;
                axis.labels.format = "{value}";
                return axis;
            },
            int: (serieYAxis) => {
                const axis = deepCopy(serieYAxis);
                axis.labels = axis.labels || {};
                delete axis.labels.formatter;
                axis.labels.format = "{value}";
                return axis;
            },
            day: (serieYAxis) => {
                const axis = deepCopy(serieYAxis);
                axis.labels = axis.labels || {};
                delete axis.labels.formatter;
                axis.labels.format = "{value} dias";
                return axis;
            }
        };

        if (Array.isArray(structure.yAxis)) {
            return structure.yAxis.map((yAxisItem: any) => {
                if (yAxisItem.labels?.formatter === "currency") {
                    return units["currency"](yAxisItem);
                }
                return yAxisItem;
            });
        }

        if (series[0].props?.unit && units[series[0].props?.unit]) {
            return units[series[0].props.unit](structure.yAxis);
        }

        return structure.yAxis;
    }
}

export interface IChartCustomCategoriesComponent {
    series: SerieEntity[];
    points: PointEntity[];
    visualization: VisualizationEntity;
    options: VisualizationOptionsType;
    company?: CompanyEntity;
}
